import { ReactElement, ReactNode, useState } from 'react';
import { Controller, useFormContext, useFormState } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { colors } from '~/styles/colors';
import {
  HYSTERESIS_MAX, LTI_MAX, LTI_MIN, PRIMARY_CONDENSER_OPTIONS, STAGE_DELAY_MAX, STAGE_DELAY_MIN,
} from '../constants';
import {
  FieldLabel, HelperText, mobileQuery, NativeSelect, SelectWrapper,
} from '../ds';
import {
  defaultLtiText, validateHysteresis, validateLti, validateStageDelay,
} from '../helpers';
import { DamEcoSimplifiedFormValues } from '../types';
import { Toggle } from './controls';
import { ChevronDownIcon, GearIcon } from './icons';
import { StepperField } from './StepperField';

const STROKE = colors.stroke['soft-200'];

const Box = styled.div`
  margin-top: 22px;
  overflow: hidden;
  border: 1px solid ${STROKE};
  border-radius: 10px;
`;

const Header = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 16px 20px;
  border: none;
  background: ${colors.celsius['surface-subtle']};
  font-family: inherit;
  text-align: left;
  cursor: pointer;

  ${mobileQuery} {
    flex-wrap: wrap;
    gap: 10px;
    padding: 14px 16px;
  }
`;

const HeaderTitle = styled.span`
  font-size: 15px;
  font-weight: 600;
  color: ${colors.text['main-900']};
`;

const HeaderSubtitle = styled.span`
  font-size: 13px;
  color: ${colors.text['sub-350']};

  ${mobileQuery} {
    order: 4;
    flex-basis: 100%;
  }
`;

const Chevron = styled.span<{ $open: boolean }>`
  display: flex;
  margin-left: auto;
  transition: transform 0.3s;
  transform: rotate(${({ $open }) => ($open ? '180deg' : '0deg')});
`;

const Body = styled.div`
  padding: 24px 20px;
  border-top: 1px solid ${STROKE};
  background: ${colors.bg['white-0']};

  ${mobileQuery} {
    padding: 18px 16px;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 24px;
  align-items: start;

  & + & {
    margin-top: 24px;
  }

  ${mobileQuery} {
    grid-template-columns: minmax(0, 1fr);
    gap: 18px;

    & + & {
      margin-top: 18px;
    }
  }
`;

const CondenserLabel = styled(FieldLabel)`
  align-items: flex-start;
  min-height: 32px;

  ${mobileQuery} {
    min-height: 0;
  }
`;

const ADVANCED_FIELDS: (keyof DamEcoSimplifiedFormValues)[] = ['upperHysteresis', 'lowerHysteresis', 'lti', 'stageAddDelay', 'stageReduceDelay'];

/** Interruptor do LTI, ao lado do rótulo: desligar é a forma explícita de não usar o limite. */
function LtiToggle(): ReactElement {
  const { t } = useTranslation();
  const {
    control, getValues, setValue, trigger,
  } = useFormContext<DamEcoSimplifiedFormValues>();

  function onToggle(enabled: boolean, onChange: (value: boolean) => void): void {
    onChange(enabled);
    // Ligar sem nada digitado já sugere um valor, para o campo não abrir vazio e inválido.
    if (enabled && !getValues('lti').trim()) {
      setValue('lti', defaultLtiText(getValues('setpoint')), { shouldDirty: true });
    }
    trigger('lti');
  }

  return (
    <Controller
      control={control}
      name="ltiEnabled"
      render={({ field }) => (
        <Toggle
          checked={field.value}
          onChange={(enabled) => onToggle(enabled, field.onChange)}
          label={t('ecoSimplificadoLtiAtivar')}
          testId="eco-simplificado-ltiEnabled"
        />
      )}
    />
  );
}

interface AdvancedOptionsProps {
  /** Ilustrações do design, abaixo dos campos. */
  illustrations?: ReactNode;
}

export function AdvancedOptions({ illustrations }: Readonly<AdvancedOptionsProps>): ReactElement {
  const { t } = useTranslation();
  const { control, watch } = useFormContext<DamEcoSimplifiedFormValues>();
  const { errors } = useFormState({ control });
  const [open, setOpen] = useState(false);
  const ltiEnabled = watch('ltiEnabled');

  // Um erro de validação aqui abre o painel, para o usuário ver o campo a corrigir.
  const isOpen = open || ADVANCED_FIELDS.some((field) => !!errors[field]);
  const minutes = t('ecoSimplificadoMin');
  const delayRange = t('ecoSimplificadoFaixaAtraso', { min: STAGE_DELAY_MIN, max: STAGE_DELAY_MAX });

  return (
    <Box>
      <Header type="button" onClick={() => setOpen((prev) => !prev)} aria-expanded={isOpen} aria-controls="eco-simplificado-opcoes-avancadas">
        <GearIcon color={colors.brand.primary.base} strokeWidth={1.8} />
        <HeaderTitle>{t('ecoSimplificadoOpcoesAvancadas')}</HeaderTitle>
        <HeaderSubtitle>{t('ecoSimplificadoHistereseIntervaloComando')}</HeaderSubtitle>
        <Chevron $open={isOpen}><ChevronDownIcon size={20} color={colors.text['sub-350']} /></Chevron>
      </Header>
      <Body id="eco-simplificado-opcoes-avancadas" hidden={!isOpen}>
        <Grid>
          <StepperField
            name="upperHysteresis"
            label={t('histereseSuperior')}
            unit="°C"
            limits={{ min: 0, max: HYSTERESIS_MAX, step: 1 }}
            validate={validateHysteresis}
            extra={{ hint: t('ecoSimplificadoHistereseSuperiorDica'), alignRows: true }}
          />
          <StepperField
            name="lowerHysteresis"
            label={t('histereseInferior')}
            unit="°C"
            limits={{ min: 0, max: HYSTERESIS_MAX, step: 1 }}
            validate={validateHysteresis}
            extra={{ hint: t('ecoSimplificadoHistereseInferiorDica'), alignRows: true }}
          />
          <StepperField
            name="lti"
            label={t('ecoSimplificadoLti')}
            unit="°C"
            limits={{ min: LTI_MIN, max: LTI_MAX }}
            validate={validateLti}
            disabled={!ltiEnabled}
            extra={{
              hint: <Trans i18nKey={ltiEnabled ? 'ecoSimplificadoLtiDica' : 'ecoSimplificadoLtiDesativadoDica'} components={{ b: <b /> }} />,
              alignRows: true,
              labelAction: <LtiToggle />,
            }}
          />
        </Grid>
        <Grid>
          <StepperField
            name="stageAddDelay"
            label={t('ecoSimplificadoAtrasoAdicionarEstagio')}
            unit={minutes}
            limits={{ min: STAGE_DELAY_MIN, max: STAGE_DELAY_MAX }}
            validate={validateStageDelay}
            extra={{ hint: t('ecoSimplificadoAtrasoAdicionarDica', { faixa: delayRange }), alignRows: true }}
          />
          <StepperField
            name="stageReduceDelay"
            label={t('ecoSimplificadoAtrasoReduzirEstagio')}
            unit={minutes}
            limits={{ min: STAGE_DELAY_MIN, max: STAGE_DELAY_MAX }}
            validate={validateStageDelay}
            extra={{ hint: t('ecoSimplificadoAtrasoReduzirDica', { faixa: delayRange }), alignRows: true }}
          />
          <div>
            <CondenserLabel as="label" htmlFor="eco-simplificado-condensadora">{t('ecoSimplificadoCondensadoraPrimaria')}</CondenserLabel>
            <Controller
              control={control}
              name="primaryCondenser"
              render={({ field }) => (
                <SelectWrapper>
                  <NativeSelect
                    id="eco-simplificado-condensadora"
                    data-testid="eco-simplificado-condensadora"
                    $bold
                    value={field.value}
                    onChange={(event) => field.onChange(event.target.value)}
                  >
                    {PRIMARY_CONDENSER_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>{t(option.labelKey)}</option>
                    ))}
                  </NativeSelect>
                  <ChevronDownIcon color={colors.text['sub-350']} />
                </SelectWrapper>
              )}
            />
            <HelperText>{t('ecoSimplificadoCondensadoraPrimariaDica')}</HelperText>
          </div>
        </Grid>
        {illustrations}
      </Body>
    </Box>
  );
}
